/* Digital Whiteboard — timeline day planner for dispatchers.
   Rows = trucks · Columns = 30-min time slots · Blocks = scheduled jobs.
   Drag a job block horizontally to change time_required; drag vertically to reassign truck.
   Double-click a block to open the full Edit Order dialog on the Dispatch board. */
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Grid3x3, Calendar as CalIcon, RefreshCcw } from "lucide-react";
import { mixColor } from "@/lib/mixColor";
import { localToday } from "@/lib/dates";

const SLOT_MIN = 30;                // grid granularity
const DAY_START = 6 * 60;           // 06:00 in minutes
const DAY_END = 20 * 60;            // 20:00
const SLOT_COUNT = (DAY_END - DAY_START) / SLOT_MIN;
const COL_WIDTH = 44;               // px per slot

const hhmm = (mins) => {
  const h = Math.floor(mins / 60), m = mins % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
};

const timeToMins = (t) => {
  if (!t) return DAY_START;
  const [h, m] = t.split(":").map(Number);
  return h * 60 + m;
};

export default function Planner() {
  const [date, setDate] = useState(localToday());
  const [jobs, setJobs] = useState([]);
  const [trucks, setTrucks] = useState([]);
  const [dragged, setDragged] = useState(null);
  const [loading, setLoading] = useState(true);
  const nav = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const [jRes, tRes] = await Promise.all([
        api.get(`/jobs?date=${date}`),
        api.get("/trucks"),
      ]);
      setJobs(jRes.data || []);
      setTrucks(tRes.data || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [date]);

  // Where does each job live on the grid?
  const blocks = useMemo(() => {
    const arr = [];
    for (const j of jobs) {
      if (j.status === "cancelled") continue;
      const start = timeToMins(j.time_required);
      const trucksNeeded = Math.max(1, Number(j.trucks_needed) || 1);
      const spacing = Math.max(15, Number(j.load_spacing_min) || 30);
      const durationMin = Math.max(SLOT_MIN, trucksNeeded * spacing);
      // Prefer assigned trucks; if multi-truck job, one block per assigned truck
      const assignedIds = j.assigned_truck_ids && j.assigned_truck_ids.length
        ? j.assigned_truck_ids
        : (j.truck_id ? [j.truck_id] : ["__unassigned__"]);
      assignedIds.forEach((tid, idx) => {
        arr.push({
          id: `${j.id}-${tid}-${idx}`,
          job_id: j.id,
          truck_id: tid,
          start_mins: start + (idx * spacing),
          duration_mins: spacing,
          job: j,
        });
      });
    }
    return arr;
  }, [jobs]);

  // Snap pixel offset to nearest 30-min slot
  const snapMinutes = (mins) => Math.round(mins / SLOT_MIN) * SLOT_MIN;

  const onDragStart = (e, block) => {
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", block.id);
    setDragged(block);
  };

  const onDropOnCell = async (e, truckId, slotIdx) => {
    e.preventDefault();
    if (!dragged) return;
    const newStart = DAY_START + slotIdx * SLOT_MIN;
    const newTime = hhmm(snapMinutes(newStart));
    const patch = { time_required: newTime };
    // Reassign truck if dropped on a different truck row (and not the unassigned lane)
    if (truckId && truckId !== "__unassigned__" && truckId !== dragged.truck_id) {
      patch.truck_id = truckId;   // legacy single-truck field
      if (dragged.job.assigned_truck_ids && dragged.job.assigned_truck_ids.length) {
        // Replace the specific slot in the array
        const arr = [...dragged.job.assigned_truck_ids];
        const idx = arr.findIndex((id) => id === dragged.truck_id);
        if (idx >= 0) arr[idx] = truckId; else arr.push(truckId);
        patch.assigned_truck_ids = arr;
      } else {
        patch.assigned_truck_ids = [truckId];
      }
    }
    try {
      await api.patch(`/jobs/${dragged.job_id}`, patch);
      toast.success(`Moved to ${newTime}`);
      setDragged(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Update failed");
    }
  };

  const allowDrop = (e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; };

  // Truck rows + one "unassigned" lane at the top for jobs without a truck
  const rows = [
    { id: "__unassigned__", label: "UNASSIGNED", sub: "Drop here to unassign" },
    ...trucks.map((t) => ({ id: t.id, label: t.number, sub: `${t.rego || ""} · ${t.driver_name || "—"}` })),
  ];

  return (
    <div className="p-6 lg:p-8 space-y-4" data-testid="planner-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="label-tag">DISPATCH · DIGITAL WHITEBOARD</div>
          <h1 className="heading text-3xl sm:text-4xl font-black uppercase tracking-tight">
            Day <span className="text-primary-orange">Planner</span>
          </h1>
          <p className="text-muted-industrial text-sm mt-1">Drag job blocks to reschedule or reassign trucks. Snaps to 30-min slots.</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1 border border-industrial px-2 py-1 bg-surface rounded-lg">
            <CalIcon className="w-3.5 h-3.5 text-primary-orange" />
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="rounded-none bg-transparent border-0 mono h-7 px-2 w-[145px] text-xs focus-visible:ring-0" data-testid="planner-date" />
          </div>
          <Button data-testid="planner-refresh" onClick={load} variant="outline" className="rounded-lg border-industrial hover:bg-surface-2">
            <RefreshCcw className="w-3.5 h-3.5" />
          </Button>
        </div>
      </div>

      {loading && <div className="label-tag text-primary-orange pulse-orange">Loading planner…</div>}

      <Card className="card-v2 p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <div style={{ minWidth: `${180 + SLOT_COUNT * COL_WIDTH}px` }}>
            {/* Time header */}
            <div className="flex sticky top-0 z-10 bg-surface border-b border-industrial">
              <div className="w-[180px] shrink-0 border-r border-industrial px-3 py-2 label-tag flex items-center gap-2">
                <Grid3x3 className="w-3 h-3" /> Truck
              </div>
              {Array.from({ length: SLOT_COUNT }).map((_, i) => {
                const mins = DAY_START + i * SLOT_MIN;
                const isHour = mins % 60 === 0;
                return (
                  <div key={i} style={{ width: `${COL_WIDTH}px` }} className={`shrink-0 py-2 text-center border-r border-industrial ${isHour ? "" : "opacity-50"}`}>
                    <div className="mono text-[10px] font-bold">{isHour ? hhmm(mins) : ""}</div>
                  </div>
                );
              })}
            </div>

            {/* Truck rows */}
            {rows.map((row, ridx) => (
              <div key={row.id} className="flex border-b border-industrial hover:bg-surface-2/40" data-testid={`planner-row-${row.label}`}>
                <div className="w-[180px] shrink-0 border-r border-industrial px-3 py-3">
                  <div className="heading font-black text-sm">{row.label}</div>
                  <div className="text-[10px] mono text-muted-industrial truncate">{row.sub}</div>
                </div>
                <div className="relative flex-1 flex" style={{ height: "58px" }}>
                  {Array.from({ length: SLOT_COUNT }).map((_, i) => (
                    <div
                      key={i}
                      onDragOver={allowDrop}
                      onDrop={(e) => onDropOnCell(e, row.id, i)}
                      style={{ width: `${COL_WIDTH}px` }}
                      className="shrink-0 border-r border-industrial/40 hover:bg-primary-orange/5"
                      data-testid={`cell-${row.label}-${i}`}
                    />
                  ))}
                  {/* Blocks on this row */}
                  {blocks.filter((b) => b.truck_id === row.id).map((b) => {
                    const leftSlots = (b.start_mins - DAY_START) / SLOT_MIN;
                    const widthSlots = Math.max(1, b.duration_mins / SLOT_MIN);
                    if (leftSlots < 0 || leftSlots >= SLOT_COUNT) return null;
                    const mc = mixColor(b.job.mix_code);
                    return (
                      <div
                        key={b.id}
                        draggable
                        onDragStart={(e) => onDragStart(e, b)}
                        onDoubleClick={() => nav(`/dispatch?date=${b.job.date || date}`)}
                        data-testid={`block-${b.job.id}-${b.truck_id}`}
                        style={{
                          left: `${leftSlots * COL_WIDTH}px`,
                          width: `${widthSlots * COL_WIDTH - 4}px`,
                          borderLeft: `4px solid ${mc.hex}`,
                        }}
                        className="absolute top-1 bottom-1 rounded-md bg-surface border border-industrial hover:border-primary-orange shadow-md cursor-move overflow-hidden px-2 py-1"
                        title={`${b.job.customer_name} · ${b.job.mix_code} · ${b.job.quantity_m3} m³ — double-click to edit on Dispatch board`}
                      >
                        <div className="mono text-[10px] font-bold text-primary-orange leading-none">{b.job.time_required}</div>
                        <div className="text-[11px] font-semibold truncate text-ink leading-tight mt-0.5">{b.job.customer_name}</div>
                        <div className="text-[10px] mono text-muted-industrial truncate">{b.job.mix_code} · {b.job.quantity_m3} m³</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      </Card>

      <div className="text-xs text-muted-industrial mono">
        Legend: block colour matches mix code. Drop on <span className="text-ink">UNASSIGNED</span> to remove truck assignment.
        <b> Double-click</b> any block to open the full Edit Order dialog on the Dispatch board.
      </div>
    </div>
  );
}
